import { useState } from "react";
import { ListFilter as FilterIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import {
  listFilterButtonCount,
  mobileActiveFilterCount,
  type MobileFilterSortDraft,
} from "../conversation-list-filters.js";
import { ConversationFilterSections } from "./ConversationFilterSections.js";
import { InboxQuickButton, InboxQuickCount } from "../InboxIconButton.js";

/** Desktop filters: changes apply immediately to the server-backed list query. */
export function ConversationFilterPopover({
  value,
  onChange,
  onReset,
}: {
  value: MobileFilterSortDraft;
  onChange: (next: MobileFilterSortDraft) => void;
  onReset: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const count = listFilterButtonCount(value);
  const canReset = mobileActiveFilterCount(value) > 0;
  const label = count > 0 ? t("inbox.filters.buttonActive", { count }) : t("inbox.filters.button");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltip content={label}>
        <PopoverTrigger asChild>
          <InboxQuickButton aria-label={label} aria-haspopup="dialog" active={count > 0}>
            <FilterIcon size={16} aria-hidden="true" />
            <InboxQuickCount count={count} />
          </InboxQuickButton>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[min(18.5rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border-inbox-line p-0 shadow-inbox-pop"
        aria-label={t("inbox.filters.button")}
      >
        <ConversationFilterSections value={value} onChange={onChange} />
        <div className="flex items-center justify-between gap-2 border-t border-inbox-line-soft bg-[#fafcfb] px-3.5 py-2.5">
          <button
            type="button"
            className="rounded-md px-1 py-0.5 text-[0.75rem] font-[650] text-primary hover:underline disabled:text-inbox-subtle disabled:no-underline"
            onClick={onReset}
            disabled={!canReset}
          >
            {t("inbox.filters.reset")}
          </button>
          <Button type="button" size="sm" className="h-[1.875rem] px-2.5 text-[0.78rem]" onClick={() => setOpen(false)}>
            {t("inbox.filters.done")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
